/**
 * Organizations API service - handles organization API calls
 */

import { apiClient } from './client';

// Types
export interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logoUrl?: string;
  website?: string;
  tier: 'free' | 'pro' | 'business' | 'enterprise';
  memberCount: number;
  settings?: OrganizationSettings;
  createdAt: string;
  updatedAt: string;
}

export interface OrganizationDetail extends Organization {
  settings: OrganizationSettings;
}

export interface OrganizationSettings {
  defaultProjectVisibility: 'public' | 'private';
  allowMemberInvites: boolean;
  requireEmailVerification: boolean;
  defaultTaskDueDays: number;
  defaultBoardView?: 'kanban' | 'list' | 'timeline' | 'calendar';
  allowPublicBoards?: boolean;
  requireTwoFactor?: boolean;
}

export interface OrganizationMember {
  id: string;
  userId: string;
  user: {
    id: string;
    email: string;
    displayName?: string;
    avatarUrl?: string;
  };
  role: 'owner' | 'admin' | 'member' | 'guest';
  joinedAt: string;
  isPending: boolean;
}

export interface CreateOrganizationRequest {
  name: string;
  slug?: string;
  description?: string;
}

export interface UpdateOrganizationRequest {
  name?: string;
  slug?: string;
  description?: string;
  logoUrl?: string;
  website?: string;
  settings?: Partial<OrganizationSettings>;
}

export interface InviteMemberRequest {
  email: string;
  role: 'admin' | 'member' | 'guest';
}

export interface UpdateMemberRoleRequest {
  role: 'admin' | 'member' | 'guest';
}

export interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  invitedBy: {
    id: string;
    displayName: string;
  };
  invitedAt: string;
  expiresAt: string;
}

export interface UsageSummary {
  members: { current: number; limit: number };
  projects: { current: number; limit: number };
  tasksPerProject: { limit: number };
  storageMb: { limit: number };
}

/**
 * Organizations API methods
 */
export const organizationsApi = {
  /**
   * List user's organizations
   */
  async list(): Promise<Organization[]> {
    const response = await apiClient.get<any[]>('/organizations');
    return response.data.map(transformOrganization);
  },

  /**
   * Get organization details
   */
  async get(orgId: string): Promise<OrganizationDetail> {
    const response = await apiClient.get<any>(`/organizations/${orgId}`);
    return transformOrganizationDetail(response.data);
  },

  /**
   * Create a new organization
   */
  async create(data: CreateOrganizationRequest): Promise<Organization> {
    const response = await apiClient.post<any>('/organizations', data);
    return transformOrganization(response.data);
  },

  /**
   * Update organization
   */
  async update(orgId: string, data: UpdateOrganizationRequest): Promise<Organization> {
    const response = await apiClient.patch<any>(`/organizations/${orgId}`, data);
    return transformOrganization(response.data);
  },

  /**
   * Delete organization
   */
  async delete(orgId: string): Promise<void> {
    await apiClient.delete(`/organizations/${orgId}`);
  },

  /**
   * List organization members
   */
  async listMembers(orgId: string): Promise<OrganizationMember[]> {
    const response = await apiClient.get<any[]>(`/organizations/${orgId}/members`);
    return response.data.map(transformMember);
  },

  /**
   * Get organization members (alias for listMembers)
   */
  async getMembers(orgId: string): Promise<OrganizationMember[]> {
    return organizationsApi.listMembers(orgId);
  },

  /**
   * Invite a member
   */
  async inviteMember(orgId: string, data: InviteMemberRequest): Promise<void> {
    await apiClient.post(`/organizations/${orgId}/invitations`, data);
  },

  /**
   * Update member role
   */
  async updateMemberRole(
    orgId: string,
    userId: string,
    role: string
  ): Promise<OrganizationMember> {
    const response = await apiClient.patch<any>(
      `/organizations/${orgId}/members/${userId}`,
      { role }
    );
    return transformMember(response.data);
  },

  /**
   * Remove member
   */
  async removeMember(orgId: string, userId: string): Promise<void> {
    await apiClient.delete(`/organizations/${orgId}/members/${userId}`);
  },

  /**
   * Leave organization (self-removal)
   */
  async leave(orgId: string): Promise<void> {
    await apiClient.post(`/organizations/${orgId}/leave`);
  },

  /**
   * List pending invitations
   */
  async listInvitations(orgId: string): Promise<PendingInvitation[]> {
    const response = await apiClient.get<any[]>(`/organizations/${orgId}/invitations`);
    return response.data.map(transformInvitation);
  },

  /**
   * Cancel an invitation
   */
  async cancelInvitation(orgId: string, invitationId: string): Promise<void> {
    await apiClient.delete(`/organizations/${orgId}/invitations/${invitationId}`);
  },

  /**
   * Resend an invitation
   */
  async resendInvitation(orgId: string, invitationId: string): Promise<void> {
    await apiClient.post(`/organizations/${orgId}/invitations/${invitationId}/resend`);
  },

  /**
   * Accept an invitation
   */
  async acceptInvitation(token: string): Promise<Organization> {
    const response = await apiClient.post<any>('/invitations/accept', { token });
    return transformOrganization(response.data);
  },

  /**
   * Get usage summary
   */
  async getUsage(orgId: string): Promise<UsageSummary> {
    const response = await apiClient.get<any>(`/organizations/${orgId}/usage`);
    return {
      members: response.data.members,
      projects: response.data.projects,
      tasksPerProject: response.data.tasksPerProject ?? response.data.tasks_per_project,
      storageMb: response.data.storageMb ?? response.data.storage_mb,
    };
  },

  /**
   * Update organization settings
   */
  async updateSettings(
    orgId: string,
    settings: Partial<OrganizationSettings>
  ): Promise<OrganizationSettings> {
    const response = await apiClient.patch<any>(
      `/organizations/${orgId}/settings`,
      transformSettingsToSnakeCase(settings)
    );
    return transformSettings(response.data);
  },
};

// Transform functions
// Note: Backend uses alias_generator=to_camel, so API responses are camelCase
function transformOrganization(data: any): Organization {
  return {
    id: data.id,
    name: data.name,
    slug: data.slug,
    description: data.description,
    logoUrl: data.logoUrl ?? data.logo_url,
    tier: data.tier,
    memberCount: data.memberCount ?? data.member_count,
    createdAt: data.createdAt ?? data.created_at,
    updatedAt: data.updatedAt ?? data.updated_at,
  };
}

function transformOrganizationDetail(data: any): OrganizationDetail {
  return {
    ...transformOrganization(data),
    settings: transformSettings(data.settings || {}),
  };
}

function transformSettings(data: any): OrganizationSettings {
  return {
    defaultProjectVisibility: data.defaultProjectVisibility ?? data.default_project_visibility ?? 'private',
    allowMemberInvites: data.allowMemberInvites ?? data.allow_member_invites ?? true,
    requireEmailVerification: data.requireEmailVerification ?? data.require_email_verification ?? false,
    defaultTaskDueDays: data.defaultTaskDueDays ?? data.default_task_due_days ?? 7,
  };
}

function transformSettingsToSnakeCase(settings: Partial<OrganizationSettings>): any {
  const result: any = {};
  if (settings.defaultProjectVisibility !== undefined) {
    result.default_project_visibility = settings.defaultProjectVisibility;
  }
  if (settings.allowMemberInvites !== undefined) {
    result.allow_member_invites = settings.allowMemberInvites;
  }
  if (settings.requireEmailVerification !== undefined) {
    result.require_email_verification = settings.requireEmailVerification;
  }
  if (settings.defaultTaskDueDays !== undefined) {
    result.default_task_due_days = settings.defaultTaskDueDays;
  }
  return result;
}

function transformMember(data: any): OrganizationMember {
  return {
    id: data.id,
    userId: data.userId ?? data.user_id ?? data.user?.id,
    user: {
      id: data.user?.id ?? data.userId ?? data.user_id,
      email: data.user?.email ?? '',
      displayName: data.user?.displayName ?? data.user?.display_name,
      avatarUrl: data.user?.avatarUrl ?? data.user?.avatar_url,
    },
    role: data.role,
    joinedAt: data.joinedAt ?? data.joined_at,
    isPending: data.isPending ?? data.is_pending ?? false,
  };
}

function transformInvitation(data: any): PendingInvitation {
  const invitedBy = data.invitedBy ?? data.invited_by;
  return {
    id: data.id,
    email: data.email,
    role: data.role,
    invitedBy: {
      id: invitedBy?.id,
      displayName: invitedBy?.displayName ?? invitedBy?.display_name,
    },
    invitedAt: data.invitedAt ?? data.invited_at,
    expiresAt: data.expiresAt ?? data.expires_at,
  };
}

export default organizationsApi;
